<template>
  <div class="apt-form">
    <!-- Body -->
    <div v-if="loading !== true">
      <!-- Gradient header, tinted live by the selected type's color -->
      <div class="apt-header" :style="{ '--apt-accent': typeMeta.color }">
        <div class="apt-header-icon">
          <feather-icon :icon="typeMeta.icon" size="20"/>
        </div>
        <div class="apt-header-text">
          <h4 class="mb-0">{{ editing ? $t('operations.edit') : $t('calendar.action.addEvent') }}</h4>
          <small v-if="editing">#{{ calendarEvent.id }} · {{ $t(`calendarTypes.${calendarEvent.type}.name`) }}</small>
          <small v-else>{{ $t(`calendarTypes.${calendarEvent.type}.name`) }} · {{ typeMeta.duration }} {{ $t('calendar.minutes') }}</small>
        </div>
        <button type="button" class="apt-header-close" @click="$emit('close')">
          <feather-icon icon="XIcon" size="18"/>
        </button>
      </div>

      <div class="apt-body">
        <!-- Cancelled / read-only banners -->
        <b-alert class="p-1" v-if="calendarEvent.status === 'CANCELLED'" variant="danger" show>
          <i class="fa-solid fa-triangle-exclamation mr-25"></i>{{ $t('calendar.canceledEvent') }}
        </b-alert>
        <b-alert class="p-1" v-else-if="!editable" variant="warning" show>
          <i class="fa-solid fa-triangle-exclamation mr-25"></i>{{ $t('calendar.readOnlyMsg') }}
        </b-alert>

        <validation-observer #default="{ invalid }" ref="refFormObserver">
          <!-- Title -->
          <validation-provider #default="validationContext" :name="$t('calendar.title')">
            <b-form-group :label="$t('calendar.title')" label-for="event-title">
              <b-form-input
                  id="event-title"
                  v-model="calendarEvent.title"
                  autofocus
                  :state="getValidationState(validationContext)"
                  :placeholder="$t('calendar.titlePlaceholder')"
                  trim
              />
              <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Type cards (color dot + name + default duration) -->
          <b-form-group :label="$t('calendar.type')">
            <div class="apt-type-grid">
              <button v-for="t in typeList" :key="t.value" type="button" class="apt-type-card"
                      :class="{ 'is-active': calendarEvent.type === t.value }"
                      :style="{ '--apt-type-color': t.color }"
                      @click="selectType(t)">
                <span class="apt-type-card__dot" :style="{ background: t.color }"></span>
                <span class="apt-type-card__name">{{ $t(`calendarTypes.${t.value}.name`) }}</span>
                <span class="apt-type-card__duration">{{ t.duration }} {{ $t('calendar.minutes') }}</span>
              </button>
            </div>
          </b-form-group>

          <!-- Start / End -->
          <b-form-group :label="$t('calendar.date')" label-for="event-range">
            <calendar-picker-range :need-footer="false" mode="dateTime" v-bind:value="eventRange" @input="setEventRange"/>
            <div class="apt-duration mt-25" v-if="durationLabel">
              <feather-icon icon="ClockIcon" size="13" class="mr-25"/>{{ durationLabel }}
            </div>
            <div class="apt-duration apt-duration--invalid mt-25" v-else-if="rangeInvalid">
              <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25"/>{{ $t('calendar.endBeforeStart') }}
            </div>
          </b-form-group>

          <!-- Participants -->
          <b-form-group :label="$t('calendar.representatives')" label-for="add-guests">
            <user-picker v-model="calendarEvent.users" multiple exclude-me/>
          </b-form-group>

          <!-- Org and Client -->
          <b-row>
            <b-col cols="3" v-if="!editing && hasManyOrganizations">
              <b-form-group :label="$t('columns.org')">
                <organization-select-box acronym v-model="calendarEvent.orgId" :clearable="false"/>
              </b-form-group>
            </b-col>
            <b-col v-bind:cols="!editing && hasManyOrganizations ? 9 : 12">
              <b-form-group :label="$t('calendar.clients')">
                <client-picker v-bind:org="calendarEvent.orgId" v-model="calendarEvent.clientsIds"
                               :disabled="calendarEvent.orgId == null || calendarEvent.orgId === ''"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Location + URL -->
          <b-row>
            <b-col md="6">
              <b-form-group :label="$t('calendar.location')" label-for="event-location">
                <b-form-input id="event-location" v-model="calendarEvent.location" trim/>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <validation-provider #default="validationContext" name="Event URL" rules="url">
                <b-form-group :label="$t('calendar.url')" label-for="event-url">
                  <b-form-input id="event-url" v-model="calendarEvent.url" type="url"
                                :state="getValidationState(validationContext)" trim/>
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Description -->
          <b-form-group :label="$t('calendar.description')" label-for="event-description">
            <b-form-textarea id="event-description" v-model="calendarEvent.body" rows="3" max-rows="8"/>
          </b-form-group>

          <!-- Meta (edit mode) -->
          <div class="apt-meta" v-if="editing">
            {{ $t('calendar.host') }}: <strong>{{ getUserFullName(calendarEvent.userId) }}</strong>
          </div>

          <!-- Actions -->
          <div class="apt-actions mt-1">
            <b-button v-if="editing && editable" variant="outline-danger" :disabled="saving"
                      @click="cancelThisEvent">
              <feather-icon icon="TrashIcon" size="14" class="mr-25"/>{{ $t('operations.dismiss') }}
            </b-button>
            <span class="apt-actions__spacer"></span>
            <b-button variant="outline-secondary" :disabled="saving" @click="$emit('close')">
              {{ $t('operations.cancel') }}
            </b-button>
            <b-button v-if="editable" variant="primary" type="submit"
                      :disabled="invalid || rangeInvalid || saving" @click="save">
              <b-spinner v-if="saving" small class="mr-25"/>
              {{ editing ? $t('operations.update') : $t('operations.add') }}
            </b-button>
          </div>
        </validation-observer>
      </div>
      <apt-confirm-modal ref="confirmModal"/>
    </div>
    <!-- skeleton -->
    <div class="p-2" v-else>
      <label>{{$t('calendar.title')}}</label>
      <b-skeleton type="input"></b-skeleton>
      <label>{{$t('calendar.type')}}</label>
      <b-skeleton-img no-aspect height="60px"></b-skeleton-img>
      <b-row>
        <b-col cols="6">
          <label>{{$t('calendar.startDate')}}</label>
          <b-skeleton type="input"></b-skeleton>
        </b-col>
        <b-col cols="6">
          <label>{{$t('calendar.endDate')}}</label>
          <b-skeleton type="input"></b-skeleton>
        </b-col>
      </b-row>
      <label>{{$t('calendar.representatives')}}</label>
      <b-skeleton type="input"></b-skeleton>
      <label>{{$t('calendar.clients')}}</label>
      <b-skeleton type="input"></b-skeleton>
      <label>{{$t('calendar.description')}}</label>
      <b-skeleton-img no-aspect height="50px"></b-skeleton-img>
    </div>

  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase'
import mixinValidation from '@/components/mixin/mixinValidation'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import CalendarPickerRange from '@/components/widget/picker/calendarPickerRange'
import CalendarRequest from '@/model/calendar/CalendarRequest'
import ClientPicker from '@/components/widget/picker/clientPicker'
import { mapActions, mapGetters } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import OrganizationSelectBox from '@/components/widget/select/OrganizationSelectBox'
import UserPicker from '@/components/widget/picker/userPicker'
import { CALENDAR_TYPE_META, calendarTypeMeta } from '@/model/calendar/calendarMeta'
import AptConfirmModal from '@/components/modal/AptConfirmModal'

export default {
  name: 'CalendarEvent',
  components: {
    UserPicker,
    OrganizationSelectBox,
    ClientPicker,
    CalendarPickerRange,
    ValidationProvider,
    ValidationObserver,
    AptConfirmModal
  },
  mixins: [mixinBase, mixinNotifications, mixinPermissions, mixinValidation],
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      required: true
    },
  },
  watch: {
    value: {
      handler: function (val) {
        this.refresh()
      },
      immediate: true
    },
    // Moving the start keeps the event duration (only for USER input — the
    // populating flag guards programmatic form fills).
    'calendarEvent.start': function (newVal, oldVal) {
      if (this.populating || !newVal || !oldVal || newVal === oldVal) return
      const oldStart = this.$moment(oldVal, 'YYYY-MM-DD HH:mm')
      const oldEnd = this.$moment(this.calendarEvent.end, 'YYYY-MM-DD HH:mm')
      if (!oldStart.isValid() || !oldEnd.isValid()) return
      const duration = oldEnd.diff(oldStart, 'minutes')
      if (duration <= 0) return
      const newStart = this.$moment(newVal, 'YYYY-MM-DD HH:mm')
      if (!newStart.isValid()) return
      this.calendarEvent.end = newStart.clone().add(duration, 'minutes').format('YYYY-MM-DD HH:mm')
    },
  },
  data: () => ({
    calendarEvent: CalendarRequest(),
    otherUsers: [],
    saving: false,
    populating: false,
  }),
  computed:{
    ...mapGetters('user', ['myId']),
    ...mapGetters('data', ['getUserFullName']),
    editable(){
      if(this.calendarEvent.status === 'CANCELLED'){
        return false
      }
      return !this.editing || this.calendarEvent.userId === this.myId || this.hasPermission(this.permissions.CALENDAR_UPDATE_ALL)
    },
    editing(){
      return this.calendarEvent.id != null && this.calendarEvent.id !== ''
    },
    eventRange(){
      return {
        start: this.calendarEvent.start,
        end: this.calendarEvent.end
      }
    },
    typeList() {
      const known = Object.keys(this.$t('calendarTypes'))
      return CALENDAR_TYPE_META.filter(t => known.includes(t.value))
    },
    typeMeta() {
      return calendarTypeMeta(this.calendarEvent.type)
    },
    rangeInvalid() {
      const s = this.$moment(this.calendarEvent.start, 'YYYY-MM-DD HH:mm')
      const e = this.$moment(this.calendarEvent.end, 'YYYY-MM-DD HH:mm')
      return s.isValid() && e.isValid() && !e.isAfter(s)
    },
    durationLabel() {
      const s = this.$moment(this.calendarEvent.start, 'YYYY-MM-DD HH:mm')
      const e = this.$moment(this.calendarEvent.end, 'YYYY-MM-DD HH:mm')
      if (!s.isValid() || !e.isValid() || !e.isAfter(s)) return null
      const mins = e.diff(s, 'minutes')
      const h = Math.floor(mins / 60)
      const m = mins % 60
      if (h && m) return `${h} h ${m} ${this.$t('calendar.minutes')}`
      if (h) return `${h} h`
      return `${m} ${this.$t('calendar.minutes')}`
    },
  },
  methods:{
    ...mapActions('calendar', ['addCalendar', 'updateCalendar', 'cancelEvent']),
    refresh(){
      let $this = this;
      this.populating = true
      this.calendarEvent = {
        ...this.value, users: ($this.value.users || []).map((u) => u.userId)
      }
      // Create defaults: start = now rounded UP to the next half hour, end =
      // start + selected type's default duration.
      if (this.calendarEvent.id == null || this.calendarEvent.id === '') {
        let start = this.calendarEvent.start
            ? this.$moment(this.calendarEvent.start, 'YYYY-MM-DD HH:mm')
            : this.$moment()
        if (!start.isValid()) start = this.$moment()
        const rounded = start.clone().startOf('hour')
            .add(Math.ceil(start.minute() / 30) * 30, 'minutes')
        if (!this.value.start) {
          this.calendarEvent.start = rounded.format('YYYY-MM-DD HH:mm')
        }
        if (!this.calendarEvent.end) {
          const s = this.$moment(this.calendarEvent.start, 'YYYY-MM-DD HH:mm')
          this.calendarEvent.end = s.clone()
              .add(calendarTypeMeta(this.calendarEvent.type).duration, 'minutes')
              .format('YYYY-MM-DD HH:mm')
        }
      }
      this.$nextTick(() => { this.populating = false })
    },
    // Picking a type re-tints the header and sets end = start + type duration.
    selectType(t) {
      if (!this.editable) return
      this.calendarEvent.type = t.value
      const s = this.$moment(this.calendarEvent.start, 'YYYY-MM-DD HH:mm')
      if (s.isValid()) {
        this.calendarEvent.end = s.clone().add(t.duration, 'minutes').format('YYYY-MM-DD HH:mm')
      }
    },
    setEventRange(value){
      this.calendarEvent.start = value.start
      this.calendarEvent.end = value.end
    },
    save(){
      let $this = this;
      if (this.saving) return
      this.saving = true
      let promise = this.calendarEvent.id != null ?
          this.updateCalendar({ calendar: this.calendarEvent, id: this.calendarEvent.id }) :
          this.addCalendar(this.calendarEvent);
      promise.then((calendar) => {
        $this.$emit('input', calendar)
        $this.$emit('submitted', calendar)
        $this.$emit('close')
        $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.calendar') }));
      }, err => {
        $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: $this.$t('menu.calendar') }), $this.getErrorDescription(err));
      }).finally(() => { $this.saving = false })
    },
    async cancelThisEvent() {
      const result = await this.$refs.confirmModal.open({
        title: this.$t('operations.dismiss'),
        message: this.$t('calendar.action.dismissMsg', { event: this.calendarEvent.title }),
        okText: this.$t('operations.dismiss'),
      })
      if (!result) return
      const $this = this
      this.saving = true
      this.cancelEvent(this.calendarEvent.id)
          .then(
              ev => {
                $this.$emit('input', ev)
                $this.$emit('submitted', ev)
                $this.$emit('close')
                $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.calendar') }))
              },
              err => $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: $this.$t('menu.calendar') }), $this.getErrorDescription(err))
          )
          .finally(() => { $this.saving = false })
    },
  }
}
</script>

<style lang="scss">
/* Unscoped: the modal content is portaled to <body>. Scoped under .apt-form. */
.apt-form {
  .apt-header {
    display: flex;
    align-items: center;
    gap: .9rem;
    padding: 1.1rem 1.4rem;
    background: linear-gradient(120deg, var(--apt-accent, #7367f0), #4b4b9b);
    color: #fff;

    h4 { color: #fff; font-weight: 700; }
    small { opacity: .9; }
  }
  .apt-header-icon {
    width: 44px;
    height: 44px;
    border-radius: .65rem;
    background: rgba(255, 255, 255, .18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .apt-header-text { flex: 1; min-width: 0; }
  .apt-header-close {
    border: none;
    background: rgba(255, 255, 255, .15);
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background .15s ease, transform .2s ease;
    &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
  }

  .apt-body { padding: 1.25rem 1.4rem 1.4rem; }

  /* Type cards */
  .apt-type-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
    gap: .5rem;
  }
  .apt-type-card {
    display: flex;
    align-items: center;
    gap: .45rem;
    border: 1px solid #d8d6de;
    border-radius: .55rem;
    background: transparent;
    padding: .5rem .6rem;
    cursor: pointer;
    text-align: left;
    transition: all .15s ease;

    &:hover { border-color: var(--apt-type-color); }
    &.is-active {
      border-color: var(--apt-type-color);
      background: rgba(115, 103, 240, .08);
      box-shadow: inset 0 0 0 1px var(--apt-type-color);
    }
    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    &__name {
      flex: 1;
      min-width: 0;
      font-size: .8rem;
      font-weight: 600;
      color: #5e5873;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__duration {
      font-size: .68rem;
      color: #b9b9c3;
      white-space: nowrap;
    }
  }

  .apt-duration {
    display: inline-flex;
    align-items: center;
    font-size: .78rem;
    font-weight: 600;
    color: #7367f0;

    &--invalid { color: #ea5455; }
  }

  .apt-meta {
    font-size: .78rem;
    color: #6e6b7b;
    padding-top: .25rem;
  }

  .apt-actions {
    display: flex;
    align-items: center;
    gap: .5rem;

    &__spacer { flex: 1; }
  }
}

/* Dark mode */
.dark-layout .apt-form {
  .apt-type-card {
    border-color: #3b4253;
    &__name { color: #d0d2d6; }
  }
  .apt-meta { color: #b4b7bd; }
}
</style>
